import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientAPI } from '../services/api';
import { WorkspaceLink } from "../hooks/useWsNavigate";
import api from '../services/api/client';
import Button from '../components/ui/Button';

const Analytics = () => {
    const { user, currentWorkspace } = useAuth();
    const [dashboards, setDashboards] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [loading, setLoading] = useState(true);
    const [embedUrl, setEmbedUrl] = useState(null);
    const [embedLoading, setEmbedLoading] = useState(false);

    useEffect(() => {
        const fetchDashboards = async () => {
            try {
                const response = await clientAPI.getClient(currentWorkspace?.id || user.client_id);
                const clientData = response.data?.data || response.data;
                const configs = clientData.metabase_dashboards || [];
                setDashboards(configs);
                setActiveTab(configs[0]?.metabase_id ?? null);
            } catch (err) {
                console.error('Error fetching dashboards:', err);
            } finally {
                setLoading(false);
            }
        };

        if (currentWorkspace?.id || user?.client_id) {
            fetchDashboards();
        }
    }, [user, currentWorkspace]);

    useEffect(() => {
        const fetchEmbedUrl = async () => {
            if (dashboards.length === 0 || !activeTab) return;
            const dashboard = dashboards.find(d => d.metabase_id === activeTab);
            if (!dashboard?.metabase_id) return;

            // client_id is REQUIRED now: embed_url checks the dashboard against
            // that workspace's allowlist. Without it the backend falls back to the
            // caller's DEFAULT workspace, so anyone viewing a non-default
            // workspace would be checked against the wrong list and 404 — the
            // usual missing-client_id fallback trap, but failing closed.
            const clientId = currentWorkspace?.id || user?.client_id;
            setEmbedLoading(true);
            try {
                const response = await api.get('/api/v1/metabase/embed_url', {
                    params: { dashboard_id: dashboard.metabase_id, client_id: clientId }
                });
                setEmbedUrl(response.data.embed_url);
            } catch (err) {
                console.error('Error fetching embed URL:', err);
                setEmbedUrl(null);
            } finally {
                setEmbedLoading(false);
            }
        };

        fetchEmbedUrl();
    }, [activeTab, dashboards, currentWorkspace, user]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (dashboards.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                <div className="rounded-full bg-muted p-4 mb-4">
                    <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">No dashboards configured</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    Add Metabase dashboard IDs in your workspace settings to see them here.
                </p>
                <WorkspaceLink
                    to="/settings/workspace"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    Go to Workspace Settings
                </WorkspaceLink>
            </div>
        );
    }

    const activeDashboard = dashboards.find(d => d.metabase_id === activeTab);

    return (
        <div className="h-full flex flex-col">
            {dashboards.length > 0 && (
                <div className="pl-7 pr-4 pt-4 pb-2 bg-card">
                    <div className="flex space-x-1 bg-muted rounded-lg p-1 w-fit">
                        {dashboards.map(dashboard => (
                            <Button
                                key={dashboard.metabase_id}
                                variant="tab"
                                onClick={() => setActiveTab(dashboard.metabase_id)}
                                className={activeTab === dashboard.metabase_id
                                    ? 'text-sm bg-card text-foreground shadow-sm'
                                    : 'text-sm text-muted-foreground hover:text-foreground'
                                }
                            >
                                {dashboard.name}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {/* Dashboard Embed */}
            <div className="flex-1 min-h-0">
                {embedLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : embedUrl ? (
                    <iframe
                        key={activeTab}
                        src={embedUrl}
                        title={activeDashboard?.name}
                        className="w-full h-full border-0"
                        allowFullScreen
                    />
                ) : (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                        Failed to load dashboard. Check that METABASE_SECRET_KEY is configured.
                    </div>
                )}
            </div>
        </div>
    );
};

export default Analytics;
